import React from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { DownloadIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { MenuButton } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  blue,
  lightGrey,
} from '@fortress-technology-solutions/fortress-component-library/design/index';
import useIntl from '../../../hooks/useIntl';
import messages from '../messages';
import type { ApplicationFormHeaderProps } from './types';

const MENU_BUTTON_TOOLTIP_PROPS = {
  placement: 'left',
  sx: {
    width: 'auto',
  },
};

const ApplicationFormHeader = ({
  propertyName,
  applicationId,
  propertyId,
  isLoading,
  isSaving,
  isCompleting,
  isPrinting,
  onCancel,
  onSaveAndClose,
  onUploadApplication,
  onSendForSignature,
  canSignElectronically,
  selectedSigningOption,
  isFormValid,
  onRevealBlockingFields,
  alreadySentForSignature,
  isPendingResidentApplicant,
  onPrint,
}: ApplicationFormHeaderProps) => {
  const intl = useIntl();

  const isSavingDisabled = isLoading || isSaving || isCompleting || isPrinting;
  const isCancelDisabled = isSaving || isCompleting || isPrinting;
  const isPrintDisabled = isLoading || isSaving || isCompleting || isPrinting;
  // An invalid form keeps the Complete Form button enabled: clicking it
  // marks every field that still blocks completion and focuses the first one
  // (see handleCompleteClickCapture), instead of leaving the user in front of
  // a greyed-out button with nothing on screen explaining why.
  const isCompleteButtonDisabled =
    isLoading || isSaving || isCompleting || isPrinting;
  const shouldRevealBlockingFields = !isCompleteButtonDisabled && !isFormValid;

  // MenuButton owns its toggle handler, so the click is intercepted in the
  // capture phase: the signing menu never opens while fields are invalid.
  const handleCompleteClickCapture = (event) => {
    if (!shouldRevealBlockingFields) return;

    event.stopPropagation();
    onRevealBlockingFields?.();
  };

  const handleMenuItemClick = (value) => {
    if (value === 'manual') {
      onUploadApplication?.();
    }
    if (value === 'electronic') {
      onSendForSignature?.();
    }
  };

  const electronicSigningDisabledTooltip = React.useMemo(() => {
    if (isPendingResidentApplicant)
      return intl.formatMessage(
        messages.pendingResidentElectronicSigningTooltip,
      );
    if (selectedSigningOption !== 'electronic')
      return 'Signing Method must be Electronic to send for signature';
    if (alreadySentForSignature)
      return 'Application has already been sent for signature';
    return undefined;
  }, [
    intl,
    isPendingResidentApplicant,
    selectedSigningOption,
    alreadySentForSignature,
  ]);

  return (
    <Stack
      sx={{
        zIndex: 1000,
        backgroundColor: 'background.paper',
        paddingLeft: 5,
        paddingRight: 4,
        paddingY: 2,
        width: '100%',
        boxShadow: '0 2px 6px 4px rgba(0, 0, 0, 0.15)',
      }}
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
    >
      <Stack>
        <Typography
          variant="headlineMedium"
          component="h1"
          sx={{
            color: blue.darker,
            mb: 1,
          }}
        >
          {intl.formatMessage(messages.applicationForm)}
        </Typography>

        <Typography
          variant="titleLarge"
          sx={{
            color: lightGrey.dark,
          }}
        >
          {propertyName}
        </Typography>
      </Stack>
      <Stack flexDirection="row" alignItems="center" gap={2}>
        <Button
          variant="text"
          color="primary"
          onClick={onCancel}
          disabled={isCancelDisabled}
        >
          Cancel
        </Button>
        <Button
          variant="primarySubtle"
          color="primary"
          disabled={isSavingDisabled}
          onClick={onSaveAndClose}
        >
          Save & Close
        </Button>
        <Button
          variant="primarySubtle"
          color="primary"
          disabled={isPrintDisabled}
          onClick={onPrint}
          startIcon={<DownloadIcon />}
        >
          Print
        </Button>
        <Tooltip
          title={
            shouldRevealBlockingFields
              ? 'Some fields still need attention. Click to highlight them.'
              : undefined
          }
        >
          <Box
            component="span"
            onClickCapture={handleCompleteClickCapture}
            data-testid="complete-form-button-wrapper"
          >
            <MenuButton
              variant="primary"
              disabled={isCompleteButtonDisabled}
              MenuProps={{
                sx: {
                  marginTop: 1,
                },
              }}
              onMenuItemClick={handleMenuItemClick}
              options={[
                ...(canSignElectronically
                  ? [
                      {
                        text: 'Send for Signature',
                        value: 'electronic',
                        disabled:
                          isPendingResidentApplicant ||
                          selectedSigningOption !== 'electronic' ||
                          alreadySentForSignature,
                        tooltip: electronicSigningDisabledTooltip,
                        tooltipProps: MENU_BUTTON_TOOLTIP_PROPS,
                      },
                    ]
                  : []),
                {
                  text: 'Upload Application',
                  value: 'manual',
                  disabled: selectedSigningOption !== 'manual',
                  tooltip:
                    selectedSigningOption !== 'manual'
                      ? 'Signing Method must be Manual to upload application'
                      : undefined,
                  tooltipProps: MENU_BUTTON_TOOLTIP_PROPS,
                },
              ]}
            >
              Complete Form
            </MenuButton>
          </Box>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

export default ApplicationFormHeader;
